// calendar-sync.js — bidirectional sync between markdown daily notes and
// connected calendar providers (CalDAV, iCloud, Google, Microsoft).
//
// Architecture
//   • providers/calendar-provider.js — the abstract contract.
//   • providers/{caldav,icloud,google,microsoft}-provider.js — implementations.
//   • provider-registry.js — keeps one live provider instance per accountId,
//     hydrated from Settings.get('calendars.accounts').
//   • outbox.js — durable queue for writes that fail transiently.
//   • This file — the orchestrator. Fans out across (accountId, calendarId)
//     tuples from `Settings.get('calendars.selected')`, runs per-account
//     locks in parallel, isolates per-account failures.
//
// Markdown syntax
//   Title > YYMMDD                          — all-day event
//   Title > YYMMDD YYMMDD                   — multi-day event
//   Title > YYMMDD HHMM HHMM                — timed event
//   Title > … @CalendarName                 — route to a named calendar
//   Title > … ~weekly                       — recurring (shorthand)
//   Title > … ~rrule:FREQ=WEEKLY;BYDAY=MO   — recurring (explicit)
//
// Metadata
//   `.calendar_metadata` hidden note carries one entry per synced event:
//     { _schemaVersion: 2,
//       "260514 Daily Note": { events: [
//         { accountId, providerId, calendarId,
//           etag, sequence, lastModified,
//           title, lineText, lineIndex, calendarTag, rrule?,
//         } ] } }
//   Composite key `(accountId, providerId)` identifies an event across
//   provider re-fetches and account additions.

import { getCurrentFileName, reflectActiveView } from '../core/app-state.js';
// NoteEditor is imported LAZILY at its two use sites (dynamic `import()`), NOT
// statically: a static edge would pull the whole editor bundle into the schedule
// module graph, and editor widgets read `window.CM6.view` at module eval — absent
// in non-editor (schedule/headless/test) contexts, so it throws on import there.
// madge does not count dynamic imports, and import() caches the module.
import { Settings } from '../settings/settings-store.js';
// Shared daily-note / event-line / metadata formats. This module is the WRITER
// of those byte shapes; the note-merge guards (notes/conflict-detect,
// notes/conflict-apply) import the SAME constants so they recognise exactly
// what gets written and never auto-merge calendar-managed content. Single
// source of truth in the pure utils/ leaf (audit 2026-05-31, bug 0.4).
import { CALENDAR_METADATA_NOTE, DAILY_NOTE_RE } from '../utils/calendar-format.js';
import { toYYMMDD as dateToYYMMDD } from '../utils/dates.js';
import * as _IcalCodec from './providers/ical-codec.js';
import { invalidateScheduleCache } from './schedule-cache.js';
import {
  buildMdLine,
  dailyNoteName,
  findEventLine,
  hhmmToTime,
  insertEventsUnderHeading,
  parseMarkdownEvents,
  utcDateToYYMMDD,
  yymmddToDate,
} from './calendar-md.js';

const CALENDAR_SYNC_INTERVAL = 300000; // 5 minutes
const CALENDAR_METADATA_SCHEMA_VERSION = 2;

let _calendarSyncTimer: any = null;
// In-flight sync promise: a second concurrent call awaits the same Promise
// instead of being silently dropped (so a force-sync landing during a
// periodic run isn't lost).
let _calendarSyncPromise: any = null;
// accountId → in-flight Promise. Different accounts run in parallel;
// concurrent calls for the same accountId await one Promise so we don't
// hammer a single provider with parallel fan-outs.
const _providerLocks = new Map();

// Markdown/date format layer → extracted to calendar-md.ts (imported above).

// ── Metadata store ───────────────────────────────────────────────────────────

/** @returns {Promise<any>} the parsed metadata store. */
async function loadMetadataStore() {
  const content = await window.NoteStorage.getNote(CALENDAR_METADATA_NOTE);
  let store: any = {};
  if (content) {
    try {
      store = JSON.parse(content) || {};
    } catch {}
  }
  if (store._schemaVersion !== CALENDAR_METADATA_SCHEMA_VERSION) {
    store = { _schemaVersion: CALENDAR_METADATA_SCHEMA_VERSION };
  }
  return store;
}

/**
 * @param {any} store
 * @returns {Promise<void>}
 */
async function saveMetadataStore(store: any) {
  store._schemaVersion = CALENDAR_METADATA_SCHEMA_VERSION;
  await window.NoteStorage.setNote(CALENDAR_METADATA_NOTE, JSON.stringify(store));
}

// Composite key for v2 metadata entries. Uses a NUL byte as the separator
// so an accountId or providerId containing arbitrary printable characters
// (including `::`) can't produce a collision; NUL is never present in
// well-formed JSON-encoded strings we control or in any IDP-issued
// identifier we've seen.
/**
 * @param {any} accountId
 * @param {any} providerId
 * @returns {string} the composite metadata key.
 */
function _metaKey(accountId: any, providerId: any) {
  return `${accountId}\u0000${providerId}`;
}

/** @returns {any} the Settings store. */
function _settings() {
  // The ESM `Settings` export from settings-store.js. settings-store imports
  // nothing heavy (session / notes-broadcast / encryption-state / feedback)
  // and none of those import calendar-sync, so this static import forms no
  // cycle. Mirrors outbox.js's migration off the window.Settings shim.
  return Settings;
}
/** @returns {any} the ProviderRegistry, or false. */
function _registry() {
  return typeof window !== 'undefined' && window.ProviderRegistry;
}

// Selected calendars as [{accountId, calendarId, title}]. The settings panel
// (_renderAccountBody) is the single writer.
/** @returns {any[]} selected [{ accountId, calendarId, title }]. */
function _selectedTuples() {
  const S = _settings();
  const sel = S?.get?.('calendars.selected', []) || [];
  return Array.isArray(sel) ? sel.filter((s) => s?.accountId && s.calendarId) : [];
}

// Sync window: [startDate, today+30d]. The start is the LATER of the first-ever
// sync (firstSyncDate, stamped on first run — the "don't import events older than
// the user connected" floor) and a rolling lookback (today − syncLookbackDays).
// The lookback caps the per-poll fetch/scan, which would otherwise grow
// unbounded as time passes since first connect. syncLookbackDays is a user
// setting (Calendars → Sync window); 0 = no cap (all history since first sync).
// Both stored in Settings so they survive across restarts and platforms.
/** @returns {Promise<{ startDate: Date, endDate: Date }>} */
async function _syncWindow() {
  const S = _settings();
  let first = S?.get ? S.get('calendars.firstSyncDate', null) : null;
  const today = new Date();
  if (!first) {
    first = today.toISOString();
    try {
      if (S?.set) await S.set('calendars.firstSyncDate', first);
    } catch {}
  }
  const startDate = new Date(first);
  if (Number.isNaN(startDate.getTime())) startDate.setTime(today.getTime());

  // Floor the start at the rolling lookback so old polls don't rescan years of
  // history. Never fetch earlier than firstSyncDate (the later of the two wins).
  const lookbackDays = Number(S?.get ? S.get('calendars.syncLookbackDays', 90) : 90) || 0;
  if (lookbackDays > 0) {
    const rollingStart = new Date(today);
    rollingStart.setDate(rollingStart.getDate() - lookbackDays);
    if (rollingStart.getTime() > startDate.getTime()) startDate.setTime(rollingStart.getTime());
  }

  const endDate = new Date(today);
  endDate.setDate(endDate.getDate() + 30);
  return { startDate, endDate };
}

// Build a title → {accountId, calendarId, writable} map by querying every
// hydrated provider once. Used both for @CalendarName routing and to render
// per-event tags coming back from a fetch.
/** @returns {Promise<{ map: Map<string, any>, byCalKey: Map<string, string> }>} */
async function _buildTitleMap() {
  const map = new Map();
  const byCalKey = new Map(); // _metaKey(accountId, calendarId) → title
  const R = _registry() || null; // drop the `false` arm so R?.list narrows cleanly
  if (!R?.list) return { map, byCalKey };
  for (const { accountId, instance } of R.list()) {
    try {
      const cals = await instance.listCalendars();
      for (const c of cals || []) {
        const title = c.title || '';
        byCalKey.set(_metaKey(accountId, c.id), title);
        if (title) {
          const k = title.toLowerCase().replace(/\s+/g, '');
          // Last-writer-wins on collisions; the user can disambiguate by
          // setting an explicit default in Settings.
          map.set(k, { accountId, calendarId: c.id, title, writable: !!c.writable });
        }
      }
    } catch (e: any) {
      // One bad provider doesn't break the map for the others.
      console.warn('[calendar-sync] listCalendars failed:', accountId, e?.code || e);
    }
  }
  return { map, byCalKey };
}

// Default destination for new events when no @tag is supplied. Falls back to
// the first writable calendar across all providers; null when nothing is
// writable (caller skips create).
/**
 * @param {Map<string, any>} titleMap
 * @returns {any} { accountId, calendarId } or null.
 */
function _defaultDestination(titleMap: any) {
  const S = _settings();
  const explicit = S?.get ? S.get('calendars.default', null) : null;
  if (explicit?.accountId && explicit.calendarId) {
    return { accountId: explicit.accountId, calendarId: explicit.calendarId };
  }
  for (const v of titleMap.values()) {
    if (v.writable) return { accountId: v.accountId, calendarId: v.calendarId };
  }
  return null;
}

// Per-account lock — second concurrent call for the same accountId awaits
// the in-flight Promise. Different accounts run in parallel.
/**
 * @param {any} accountId
 * @param {() => Promise<any>} fn
 * @returns {Promise<any>}
 */
function _withAccountLock(accountId: any, fn: any) {
  const pending = _providerLocks.get(accountId);
  if (pending) return pending;
  const p = (async () => {
    try {
      return await fn();
    } finally {
      _providerLocks.delete(accountId);
    }
  })();
  _providerLocks.set(accountId, p);
  return p;
}

// In-run cache for the title map. _buildTitleMap fans `listCalendars()` out
// across every connected provider; calling it twice per sync (once in C→M,
// once in M→C) doubled the listCalendars traffic for no benefit. The cache
// lives for the duration of one `runCalendarSync` call and is cleared at
// the start of each run.
let _titleMapCache: any = null;
/** @returns {void} */
function _invalidateTitleMap() {
  _titleMapCache = null;
}
/** @returns {Promise<any>} the (cached) title map. */
async function _titleMap() {
  if (_titleMapCache) return _titleMapCache;
  _titleMapCache = await _buildTitleMap();
  return _titleMapCache;
}

// ── Sync: Calendar → Markdown ────────────────────────────────────────────────
//   Phase 0 — fan out fetches across (account, calendar) tuples; build a
//             remote event map keyed by (accountId, providerId).
//   Phase 1 — reflect remote edits / removals into existing notes for events
//             we already track in metadata. Un-checking a calendar does NOT
//             wipe its lines — only a true server-side delete drops a line.
//   Phase 2 — import new remote events. Title+time dedup binds a freshly-
//             fetched event onto an existing user-typed markdown line so
//             concurrent both-sides authoring doesn't duplicate.

/** @returns {Promise<void>} */
async function syncCalendarToMarkdown() {
  const R = _registry();
  if (!R) return;
  const selected = _selectedTuples();
  if (selected.length === 0) return;

  const store = await loadMetadataStore();
  let storeModified = false;
  const { startDate, endDate } = await _syncWindow();
  const startYYMMDD = dateToYYMMDD(startDate);
  const endYYMMDD = dateToYYMMDD(endDate);

  const byAccount = new Map();
  for (const sel of selected) {
    if (!byAccount.has(sel.accountId)) byAccount.set(sel.accountId, []);
    byAccount.get(sel.accountId).push(sel);
  }

  const { map: titleMap, byCalKey: titleByCalKey } = await _titleMap();
  const defaultDest = _defaultDestination(titleMap);

  // Parallel across accounts, serialised within. Failures isolated per
  // account+calendar. `cleanFetches` records every (accountId, calendarId)
  // that returned successfully so Phase 1 can tell "server says event is
  // gone" from "we failed to ask the server" — the difference matters
  // because the former should delete the markdown line while the latter
  // must NOT (one bad sync would otherwise wipe the user's events).
  const cleanFetches = new Set();
  // Accounts whose provider does INCREMENTAL/delta sync (Google, Microsoft):
  // after the first window fetch they persist a syncToken/deltaLink on the
  // REUSED provider instance, so a 2nd+ fetch in the same session returns ONLY
  // changed events (empty on a no-op). For these, "absent from this fetch" does
  // NOT mean the server deleted the event — see the Phase-1 delete guard.
  const deltaAccounts = new Set<string>();
  // _metaKey(accountId, calendarId) → Set<providerId> the provider AFFIRMATIVELY
  // reported as deleted (tombstones). The only safe delete signal for a delta
  // provider; non-delta providers still use absence (full-fetch invariant).
  const deletedByCalKey = new Map<string, Set<string>>();
  const fetchResults = await Promise.all(
    Array.from(byAccount.entries()).map(([accountId, sels]) =>
      _withAccountLock(accountId, async () => {
        const inst = R.get?.(accountId);
        if (!inst) return null;
        if (inst.capabilities?.delta) deltaAccounts.add(accountId);
        const out = [];
        for (const sel of sels) {
          try {
            const { events, deletedIds } = await inst.fetchEvents({
              calendarId: sel.calendarId,
              startDate: startDate.toISOString(),
              endDate: endDate.toISOString(),
            });
            out.push({ accountId, calendarId: sel.calendarId, events: events || [] });
            cleanFetches.add(_metaKey(accountId, sel.calendarId));
            if (Array.isArray(deletedIds) && deletedIds.length) {
              const ck = _metaKey(accountId, sel.calendarId);
              let tomb = deletedByCalKey.get(ck);
              if (!tomb) {
                tomb = new Set<string>();
                deletedByCalKey.set(ck, tomb);
              }
              for (const id of deletedIds) tomb.add(id);
            }
          } catch (e: any) {
            console.warn(
              '[calendar-sync] fetchEvents failed:',
              accountId,
              sel.calendarId,
              e?.code || e?.message || e
            );
          }
        }
        return out;
      })
    )
  );

  const remoteEventMap = new Map();
  for (const acctResults of fetchResults) {
    if (!acctResults) continue;
    for (const { accountId, calendarId, events } of acctResults) {
      for (const evt of events) {
        if (!evt.providerId) continue;
        remoteEventMap.set(_metaKey(accountId, evt.providerId), { accountId, calendarId, evt });
      }
    }
  }

  // Sync window as ISO bounds — used to clip recurrence expansion (here and in
  // Phase 2).
  const syncWindowRange = { from: startDate.toISOString(), to: endDate.toISOString() };

  // RECURRENCE-ID overrides: a moved/edited single instance of a series arrives
  // as its OWN event (masterProviderId + recurrenceId set, no rrule). Inject
  // each override's recurrenceId as an EXDATE on its master so the master's
  // expansion drops the original-slot occurrence — the override is then imported
  // separately at its new time, instead of the day showing BOTH slots.
  const _overridesByMaster = new Map<string, Set<string>>();
  for (const { accountId, evt } of remoteEventMap.values()) {
    if (evt.masterProviderId && evt.recurrenceId) {
      const mk = _metaKey(accountId, evt.masterProviderId);
      if (!_overridesByMaster.has(mk)) _overridesByMaster.set(mk, new Set());
      (_overridesByMaster.get(mk) as Set<string>).add(evt.recurrenceId);
    }
  }
  for (const [mk, entry] of remoteEventMap) {
    const overrides = _overridesByMaster.get(mk);
    if (overrides && entry.evt?.rrule) {
      entry.evt = { ...entry.evt, exdate: [...(entry.evt.exdate || []), ...overrides] };
    }
  }

  // Expand each recurring master's occurrences ONCE for this run. Both the
  // Phase-1 still-live occurrence-key set and the Phase-2 date bucketing consume
  // the SAME expansion over the SAME window with the SAME (post-EXDATE-injection)
  // event, so computing it twice just doubled the rrule fan-out (per-occurrence
  // allocation + tzid Intl cost) per 5-minute sync. `null` marks an entry that
  // isn't expanded (non-recurring, missing dates, or no expander) — consumers
  // fall back to `[evt]` / skip exactly as the two inline expressions used to.
  // Mirrors the in-run `_titleMap` cache rationale.
  const _occByEntry = new Map<any, any[] | null>();
  for (const entry of remoteEventMap.values()) {
    const evt = entry.evt;
    _occByEntry.set(
      entry,
      evt?.rrule && evt.startDate && evt.endDate && _IcalCodec?.expandRrule
        ? _IcalCodec.expandRrule(evt, syncWindowRange)
        : null
    );
  }

  // Synthetic occurrence keys (`${providerId}#${YYMMDD}`) for every recurring
  // master's CURRENT occurrences. Phase 2 stores each occurrence under such a
  // synthetic providerId; Phase 1 must recognise it as still-live (the master
  // still expands to it this window) rather than looking it up in remoteEventMap
  // — which is keyed by the BARE master id — missing, and "server-deleting" it.
  // Without this set every recurring occurrence flaps (delete + re-import) on
  // every sync, wiping user edits and churning writes.
  const remoteOccurrenceKeys = new Set<string>();
  for (const entry of remoteEventMap.values()) {
    const { accountId, evt } = entry;
    if (!evt.rrule || !evt.startDate || !evt.endDate) continue;
    const occs = _occByEntry.get(entry);
    if (!occs) continue;
    for (const occ of occs) {
      if (!occ.startDate) continue;
      const occStart = new Date(occ.startDate);
      if (Number.isNaN(occStart.getTime())) continue;
      const isAllDay = occ.allDay !== undefined ? occ.allDay : evt.allDay;
      const dateStr = isAllDay ? utcDateToYYMMDD(occStart) : dateToYYMMDD(occStart);
      remoteOccurrenceKeys.add(_metaKey(accountId, `${evt.providerId}#${dateStr}`));
    }
  }

  function _calTagFor(accountId: any, calendarId: any) {
    const isDefault =
      defaultDest && defaultDest.accountId === accountId && defaultDest.calendarId === calendarId;
    if (isDefault) return null;
    const t = titleByCalKey.get(_metaKey(accountId, calendarId)) || '';
    return (t || '').replace(/\s+/g, '') || null;
  }

  // Phase 1: reflect remote edits / removals into existing notes.
  const noteNames = Object.keys(store).filter((k) => !k.startsWith('_'));
  for (const noteName of noteNames) {
    const nm = noteName.match(DAILY_NOTE_RE);
    if (!nm) continue;
    const noteDate = nm[1];
    if (noteDate < startYYMMDD || noteDate > endYYMMDD) continue;

    const meta = store[noteName];
    if (!meta || !Array.isArray(meta.events) || meta.events.length === 0) continue;

    const content = await window.NoteStorage.getNote(noteName);
    if (!content) continue;
    if (content.startsWith('enc:v1:')) continue;

    const lines = content.split('\n');
    let noteModified = false;
    const lineIndicesToRemove = [];
    // Line indices already bound to a metadata entry this pass, so two
    // byte-identical event lines (same title+time on the same day) don't both
    // resolve to the first match (which would delete/overwrite the wrong row).
    const claimed = new Set<number>();

    for (let i = meta.events.length - 1; i >= 0; i--) {
      const me = meta.events[i];
      if (!me.providerId || !me.accountId) continue;
      const remote = remoteEventMap.get(_metaKey(me.accountId, me.providerId));

      if (!remote) {
        // A recurring occurrence is stored under a synthetic `${masterId}#${YYMMDD}`
        // providerId that is NOT a key in remoteEventMap (keyed by the bare master
        // id). It's still server-backed when its master expands to it this window;
        // leave the line untouched. (Edits to an occurrence line flow back as a
        // master edit on the next M→C sync — see the Phase 2 note.)
        if (
          typeof me.providerId === 'string' &&
          me.providerId.includes('#') &&
          remoteOccurrenceKeys.has(_metaKey(me.accountId, me.providerId))
        ) {
          continue;
        }
        // Reasons `remote` can be missing:
        //   (a) user unchecked this calendar — leave the line, it's not
        //       authoritative that the server deleted anything;
        //   (b) the fetch for this (account, calendar) failed — leave the
        //       line, we have no information either way;
        //   (c) the fetch succeeded and the server returned nothing for
        //       this providerId — for a FULL-FETCH provider the user really
        //       did delete it on the other side; drop the line.
        //   (d) the provider does INCREMENTAL/delta sync and this event was
        //       simply UNCHANGED, so it's absent from the delta — this is NOT
        //       a deletion. For delta accounts, absence carries no delete
        //       signal at all; only an affirmative tombstone (deletedIds) does.
        // (c) needs BOTH a current selection entry AND a clean fetch; (d) makes
        // absence-based deletion unsafe for delta accounts — guard it below.
        const stillSelected = selected.some(
          (s) => s.accountId === me.accountId && s.calendarId === me.calendarId
        );
        if (!stillSelected) continue;
        if (!cleanFetches.has(_metaKey(me.accountId, me.calendarId))) continue;
        // Delta providers return only CHANGED events, so a tracked event's
        // absence means "unchanged", not "deleted". Delete such an event ONLY
        // when the provider affirmatively tombstoned it (its bare providerId —
        // for a recurring occurrence, the master id before `#YYMMDD` — appears
        // in this calendar's deletedIds). Never delete a delta event on mere
        // absence, which would wipe every unchanged line on the 2nd+ sync.
        if (deltaAccounts.has(me.accountId)) {
          const tomb = deletedByCalKey.get(_metaKey(me.accountId, me.calendarId));
          const hashAt = typeof me.providerId === 'string' ? me.providerId.lastIndexOf('#') : -1;
          const baseId = hashAt >= 0 ? me.providerId.slice(0, hashAt) : me.providerId;
          if (!tomb || (!tomb.has(me.providerId) && !tomb.has(baseId))) continue;
        }
        const lineIdx = findEventLine(lines, me, claimed);
        if (lineIdx !== -1) {
          lineIndicesToRemove.push(lineIdx);
          claimed.add(lineIdx);
        }
        meta.events.splice(i, 1);
        noteModified = true;
      } else {
        const { calendarId, evt } = remote;
        const evtStart = new Date(evt.startDate);
        const evtEnd = new Date(evt.endDate);
        if (Number.isNaN(evtStart.getTime()) || Number.isNaN(evtEnd.getTime())) continue;
        const newDateStr = evt.allDay ? utcDateToYYMMDD(evtStart) : dateToYYMMDD(evtStart);
        const newCalTag = _calTagFor(me.accountId, calendarId);
        const newMdLine = buildMdLine(evt, evtStart, evtEnd, newDateStr, newCalTag);

        if (newMdLine !== me.lineText) {
          if (newDateStr !== noteDate) {
            // Event moved date → strip from this note, let Phase 2 re-insert.
            const lineIdx = findEventLine(lines, me, claimed);
            if (lineIdx !== -1) {
              lineIndicesToRemove.push(lineIdx);
              claimed.add(lineIdx);
            }
            meta.events.splice(i, 1);
            noteModified = true;
          } else {
            const lineIdx = findEventLine(lines, me, claimed);
            if (lineIdx !== -1) {
              claimed.add(lineIdx);
              lines[lineIdx] = newMdLine;
              me.lineText = newMdLine;
              me.title = evt.title;
              me.calendarId = calendarId;
              me.calendarTag = newCalTag;
              me.etag = evt.etag;
              me.sequence = evt.sequence || 0;
              me.lastModified = evt.lastModified;
              me.rrule = evt.rrule || null;
              noteModified = true;
            }
          }
        }
      }
    }

    for (const idx of lineIndicesToRemove.sort((a, b) => b - a)) {
      lines.splice(idx, 1);
    }

    if (noteModified) {
      store[noteName] = meta;
      storeModified = true;
      const newContent = lines.join('\n');
      await window.NoteStorage.setNote(noteName, newContent);
      if (getCurrentFileName() === noteName) {
        const { NoteEditor } = await import('../editor/note-editor.js');
        NoteEditor.replaceAll(newContent);
        reflectActiveView();
      }
    }
  }

  // Phase 2: import new remote events into daily notes.
  // For events carrying an RRULE, expand into per-occurrence virtual events
  // (each with a unique providerId#YYMMDD key) so weekly/daily meetings show
  // on every relevant day, not just the master's start date. Exception
  // semantics (EXDATE round-trip, per-occurrence edits flowing back to the
  // server as RECURRENCE-ID exceptions) are future work — for now an edit
  // to an occurrence's markdown line writes back as a master edit on next
  // M→C sync, which is no worse than the prior zero-occurrences behaviour.
  const eventsByDate: Record<string, any[]> = {};
  for (const entry of remoteEventMap.values()) {
    const { accountId, calendarId, evt } = entry;
    if (!evt.startDate || !evt.endDate) continue;
    const occurrences = _occByEntry.get(entry) ?? [evt];
    for (const occ of occurrences) {
      if (!occ.startDate || !occ.endDate) continue;
      const occStart = new Date(occ.startDate);
      const occEnd = new Date(occ.endDate);
      if (Number.isNaN(occStart.getTime()) || Number.isNaN(occEnd.getTime())) continue;
      // All-day events are UTC-anchored floating dates; timed events are real
      // instants shown on the user's local day. Bucket each into the right
      // daily note accordingly so an all-day event doesn't land a day off.
      const isAllDay = occ.allDay !== undefined ? occ.allDay : evt.allDay;
      const dateStr = isAllDay ? utcDateToYYMMDD(occStart) : dateToYYMMDD(occStart);
      // Per-occurrence providerId so dedup keys differ across occurrences of
      // the same master event. The original providerId is preserved (and
      // reused by every occurrence) — Phase 1 still uses it for delete /
      // reflect logic against the server-side master.
      const occEvt = occ === evt ? evt : { ...occ, providerId: `${evt.providerId}#${dateStr}` };
      if (!eventsByDate[dateStr]) eventsByDate[dateStr] = [];
      eventsByDate[dateStr].push({ accountId, calendarId, evt: occEvt });
    }
  }

  for (const [dateStr, items] of Object.entries(eventsByDate)) {
    const noteName = dailyNoteName(dateStr);
    let content: string = (await window.NoteStorage.getNote(noteName)) ?? `# ${noteName}\n`;
    if (content.startsWith('enc:v1:')) continue;

    const meta = store[noteName] || { events: [] };
    const existingKeys = new Set(
      meta.events
        .filter((e: any) => e.accountId && e.providerId)
        .map((e: any) => _metaKey(e.accountId, e.providerId))
    );
    let modified = false;
    const toInsert = [];
    let existingMdEvents = parseMarkdownEvents(content);

    for (const { accountId, calendarId, evt } of items) {
      const k = _metaKey(accountId, evt.providerId);
      if (existingKeys.has(k)) continue;

      const evtStart = new Date(evt.startDate);
      const evtEnd = new Date(evt.endDate);
      const calendarTag = _calTagFor(accountId, calendarId);
      const mdLine = buildMdLine(evt, evtStart, evtEnd, dateStr, calendarTag);

      // Title+time content dedup. Binds a freshly-fetched event to an
      // existing user-typed markdown line instead of duplicating the row
      // when a user adds events from both sides.
      const duplicate = existingMdEvents.find((e) => {
        if (e.text !== evt.title) return false;
        if (evt.allDay) {
          return e.allDay && Math.abs(e.startDate.getTime() - evtStart.getTime()) < 86400000;
        }
        return (
          !e.allDay &&
          Math.abs(e.startDate.getTime() - evtStart.getTime()) < 60000 &&
          Math.abs(e.endDate.getTime() - evtEnd.getTime()) < 60000
        );
      });

      if (duplicate) {
        const curLines = content.split('\n');
        if (!duplicate.calendarTag && calendarTag) {
          curLines[duplicate.lineIndex] =
            `${curLines[duplicate.lineIndex].trimEnd()} @${calendarTag}`;
          content = curLines.join('\n');
          existingMdEvents = parseMarkdownEvents(content);
        }
        meta.events.push({
          accountId,
          providerId: evt.providerId,
          calendarId,
          etag: evt.etag,
          sequence: evt.sequence || 0,
          lastModified: evt.lastModified,
          title: evt.title,
          lineText: curLines[duplicate.lineIndex].trim(),
          lineIndex: duplicate.lineIndex,
          calendarTag: duplicate.calendarTag || calendarTag,
          rrule: evt.rrule || null,
        });
        existingKeys.add(k);
        modified = true;
      } else {
        toInsert.push({ mdLine, accountId, calendarId, evt, calendarTag });
      }
    }

    if (toInsert.length > 0) {
      const result = insertEventsUnderHeading(
        content,
        toInsert.map((e) => e.mdLine)
      );
      content = result.content;
      for (let i = 0; i < toInsert.length; i++) {
        const { mdLine, accountId, calendarId, evt, calendarTag } = toInsert[i];
        meta.events.push({
          accountId,
          providerId: evt.providerId,
          calendarId,
          etag: evt.etag,
          sequence: evt.sequence || 0,
          lastModified: evt.lastModified,
          title: evt.title,
          lineText: mdLine,
          lineIndex: result.startLineIdx + i,
          calendarTag,
          rrule: evt.rrule || null,
        });
        existingKeys.add(_metaKey(accountId, evt.providerId));
      }
      modified = true;
    }

    if (modified) {
      store[noteName] = meta;
      storeModified = true;
      await window.NoteStorage.setNote(noteName, content);
      if (getCurrentFileName() === noteName) {
        const { NoteEditor } = await import('../editor/note-editor.js');
        NoteEditor.replaceAll(content);
        reflectActiveView();
      }
    }
  }

  if (storeModified) await saveMetadataStore(store);
}

// ── Sync: Markdown → Calendar ────────────────────────────────────────────────
// Four-step matching algorithm per note:
//   1. Exact lineText match — unchanged event, skip.
//   2. Same lineIndex, text changed — updateEvent (carries If-Match etag).
//   4. Unmatched metadata entry — line was removed, deleteEvent.
//   3. Unmatched current event — createEvent (in default destination, or
//      whichever calendar the @CalendarName tag resolves to).
//
// Step 4 runs before Step 3 so a freshly-created entry can't be matched as
// a stale deletion. Failed transient writes route to the outbox; hard
// failures surface via sendNotification.

/** @returns {Promise<void>} */
async function syncMarkdownToCalendar() {
  const R = _registry();
  if (!R) return;
  if (_selectedTuples().length === 0) return;

  const { map: titleMap } = await _titleMap();
  const defaultDest = _defaultDestination(titleMap);

  const { startDate } = await _syncWindow();
  const firstSyncYYMMDD = dateToYYMMDD(startDate);

  const store = await loadMetadataStore();
  let storeModified = false;

  /** @param {string | null} [tag] */
  function resolveDest(tag: any) {
    if (tag) {
      const v = titleMap.get(tag.toLowerCase().replace(/\s+/g, ''));
      if (v) return { accountId: v.accountId, calendarId: v.calendarId };
    }
    return defaultDest;
  }

  const allNotes = await window.NoteStorage.getAllNotes();
  const failedEvents = [];

  for (const { name, content } of allNotes) {
    const m = name.match(DAILY_NOTE_RE);
    if (!m) continue;
    const noteDate = m[1];
    if (noteDate < firstSyncYYMMDD) continue;
    if (content?.startsWith('enc:v1:')) continue;

    const mdEvents = parseMarkdownEvents(content);
    const meta = store[name] || { events: [] };

    if (mdEvents.length === 0 && meta.events.length === 0) continue;

    const lines = content.split('\n');
    let modified = false;
    const matchedMetaIndices = new Set();
    const matchedCurrentIndices = new Set();

    // Step 1: exact lineText matches.
    for (let ci = 0; ci < mdEvents.length; ci++) {
      const evt = mdEvents[ci];
      const lineText = evt.lineIndex < lines.length ? lines[evt.lineIndex].trim() : '';
      // Skip metadata indices already claimed this pass (mirrors Step 2 / the
      // Phase-1 `claimed` set) so two byte-identical event lines backed by two
      // distinct metadata entries pair with DISTINCT entries — otherwise both
      // resolve to the first match, leaving the second unmatched for Step 4 to
      // delete a live server-side event the user never removed.
      const metaIdx = meta.events.findIndex(
        (e: any, i: any) => !matchedMetaIndices.has(i) && e.lineText === lineText
      );
      if (metaIdx !== -1) {
        matchedMetaIndices.add(metaIdx);
        matchedCurrentIndices.add(ci);
        if (meta.events[metaIdx].lineIndex !== evt.lineIndex) {
          meta.events[metaIdx].lineIndex = evt.lineIndex;
          modified = true;
        }
      }
    }

    // Step 2: same lineIndex, text changed → updateEvent.
    for (let ci = 0; ci < mdEvents.length; ci++) {
      if (matchedCurrentIndices.has(ci)) continue;
      const evt = mdEvents[ci];
      const lineText = evt.lineIndex < lines.length ? lines[evt.lineIndex].trim() : '';
      if (!evt.text || !evt.startDate || !evt.endDate) continue;
      if (Number.isNaN(evt.startDate.getTime()) || Number.isNaN(evt.endDate.getTime())) continue;

      const metaIdx = meta.events.findIndex(
        (e: any, i: any) => !matchedMetaIndices.has(i) && e.lineIndex === evt.lineIndex
      );
      if (metaIdx === -1) continue;
      const existing = meta.events[metaIdx];
      // @CalendarName change → let Step 4 + Step 3 delete-and-recreate.
      if ((existing.calendarTag ?? null) !== evt.calendarTag) continue;

      const inst = R.get?.(existing.accountId);
      if (!inst) {
        failedEvents.push(evt.text);
        continue;
      }

      try {
        const result = await inst.updateEvent(
          existing.calendarId,
          existing.providerId,
          {
            title: evt.text,
            startDate: evt.startDate.toISOString(),
            endDate: evt.endDate.toISOString(),
            allDay: evt.allDay,
            rrule: evt.rrule || null,
          },
          { etag: existing.etag }
        );
        meta.events[metaIdx] = {
          accountId: existing.accountId,
          providerId: existing.providerId,
          calendarId: existing.calendarId,
          etag: result?.etag || existing.etag,
          sequence:
            result && result.sequence != null ? result.sequence : (existing.sequence || 0) + 1,
          lastModified: result?.lastModified || new Date().toISOString(),
          title: evt.text,
          lineText,
          lineIndex: evt.lineIndex,
          calendarTag: evt.calendarTag,
          rrule: evt.rrule || null,
        };
        modified = true;
      } catch (e: any) {
        const queued = await _maybeEnqueueFailure(
          {
            accountId: existing.accountId,
            calendarId: existing.calendarId,
            op: 'update',
            args: {
              providerId: existing.providerId,
              etag: existing.etag,
              event: {
                title: evt.text,
                startDate: evt.startDate.toISOString(),
                endDate: evt.endDate.toISOString(),
                allDay: evt.allDay,
                rrule: evt.rrule || null,
              },
            },
          },
          e
        );
        if (!queued) {
          console.warn('[calendar-sync] updateEvent failed:', e?.code || e?.message || e);
          failedEvents.push(evt.text);
        }
      }
      matchedMetaIndices.add(metaIdx);
      matchedCurrentIndices.add(ci);
    }

    // Step 4: unmatched meta entries → deleteEvent.
    // Only PRUNE the tracking entry when the server-side delete actually
    // happened (applied, idempotent NOT_FOUND, or safely handed to the durable
    // outbox), or when the account is genuinely gone from Settings (mirrors
    // outbox._applyOne's `stillRegistered` split). Otherwise KEEP the entry:
    // pruning it while the server event survives orphans that event, and the
    // NEXT C→M pass re-imports it — resurrecting a line the user deleted.
    const unmatchedMeta = [];
    for (let i = 0; i < meta.events.length; i++) {
      if (!matchedMetaIndices.has(i)) unmatchedMeta.push(i);
    }
    const prunedIndices = [];
    for (const i of unmatchedMeta) {
      const me = meta.events[i];
      if (!me.accountId || !me.providerId) {
        // Local-only tracking entry (never had a server id) — nothing to delete
        // server-side; drop it.
        prunedIndices.push(i);
        continue;
      }
      const inst = R.get?.(me.accountId);
      if (!inst) {
        // No live provider instance. Distinguish account-removed (drop) from a
        // hydration race (keep and retry later) via the persisted account list.
        const accounts = _settings()?.get?.('calendars.accounts', []) || [];
        const stillRegistered = accounts.some((a: any) => a && a.accountId === me.accountId);
        if (!stillRegistered) prunedIndices.push(i);
        continue;
      }
      try {
        await inst.deleteEvent(me.calendarId, me.providerId, { etag: me.etag });
        prunedIndices.push(i); // applied — safe to stop tracking
      } catch (e: any) {
        if (e?.code === 'NOT_FOUND') {
          // Server already lost it — idempotent success, drop the entry.
          prunedIndices.push(i);
        } else {
          const queued = await _maybeEnqueueFailure(
            {
              accountId: me.accountId,
              calendarId: me.calendarId,
              op: 'delete',
              args: { providerId: me.providerId, etag: me.etag },
            },
            e
          );
          if (queued) {
            // The durable outbox now owns this delete and will retry against
            // the server, so it's safe to stop tracking it here.
            prunedIndices.push(i);
          } else {
            // Hard, non-queued failure — KEEP the entry so the live server
            // event isn't orphaned (and re-imported) on a later C→M pass.
            console.warn('[calendar-sync] deleteEvent failed:', e?.code || e?.message || e);
          }
        }
      }
    }
    if (prunedIndices.length > 0) {
      for (let i = prunedIndices.length - 1; i >= 0; i--) {
        meta.events.splice(prunedIndices[i], 1);
      }
      modified = true;
    }

    // Step 3: unmatched current events → createEvent.
    for (let ci = 0; ci < mdEvents.length; ci++) {
      if (matchedCurrentIndices.has(ci)) continue;
      const evt = mdEvents[ci];
      const lineText = evt.lineIndex < lines.length ? lines[evt.lineIndex].trim() : '';
      if (!evt.text || !evt.startDate || !evt.endDate) continue;
      if (Number.isNaN(evt.startDate.getTime()) || Number.isNaN(evt.endDate.getTime())) continue;

      const dest = resolveDest(evt.calendarTag);
      if (!dest) continue; // no writable destination yet — skip quietly
      const inst = R.get?.(dest.accountId);
      if (!inst) {
        failedEvents.push(evt.text);
        continue;
      }

      try {
        const result = await inst.createEvent(dest.calendarId, {
          title: evt.text,
          startDate: evt.startDate.toISOString(),
          endDate: evt.endDate.toISOString(),
          allDay: evt.allDay,
          rrule: evt.rrule || null,
        });
        meta.events.push({
          accountId: dest.accountId,
          providerId: result.providerId,
          calendarId: dest.calendarId,
          etag: result.etag,
          sequence: result.sequence || 0,
          lastModified: result.lastModified || new Date().toISOString(),
          title: evt.text,
          lineText,
          lineIndex: evt.lineIndex,
          calendarTag: evt.calendarTag,
          rrule: evt.rrule || null,
        });
        modified = true;
      } catch (e: any) {
        const queued = await _maybeEnqueueFailure(
          {
            accountId: dest.accountId,
            calendarId: dest.calendarId,
            op: 'create',
            args: {
              event: {
                title: evt.text,
                startDate: evt.startDate.toISOString(),
                endDate: evt.endDate.toISOString(),
                allDay: evt.allDay,
                rrule: evt.rrule || null,
              },
            },
          },
          e
        );
        if (!queued) {
          console.warn('[calendar-sync] createEvent failed:', e?.code || e?.message || e);
          failedEvents.push(evt.text);
        }
      }
    }

    if (modified) {
      store[name] = meta;
      storeModified = true;
    }
  }

  if (failedEvents.length > 0 && typeof window.sendNotification === 'function') {
    const names = failedEvents.slice(0, 2).join(', ') + (failedEvents.length > 2 ? '…' : '');
    window.sendNotification(
      'Calendar sync failed',
      `Could not sync ${failedEvents.length} event${failedEvents.length > 1 ? 's' : ''}: ${names}`,
      `cal-sync-fail-${Date.now()}`
    );
  }

  if (storeModified) await saveMetadataStore(store);
}

// ── Orchestrator ─────────────────────────────────────────────────────────────
//
// Drains the outbox FIRST so any locally-queued writes from previous offline
// sessions land on the server before we pull. Otherwise a concurrent edit on
// another device could be reconciled against local "draft" state and
// clobber.

/** @returns {Promise<any>} the in-flight (deduped) sync promise. */
async function runCalendarSync() {
  // Concurrent calls collapse onto one Promise (matches the dedupe pattern
  // in workspace-switcher.js).
  if (_calendarSyncPromise) return _calendarSyncPromise;
  _calendarSyncPromise = (async () => {
    try {
      const R = _registry();
      if (!R) {
        console.warn('[calendar-sync] ProviderRegistry not loaded');
        return;
      }
      try {
        if (R.init) R.init();
      } catch {}
      try {
        if (R.reload) await R.reload();
      } catch (e: any) {
        console.warn('[calendar-sync] registry reload failed:', e);
      }
      // The title map is rebuilt from scratch each sync run (calendar
      // names + writability can change between runs), but is reused
      // across the C→M and M→C halves of the same run.
      _invalidateTitleMap();

      const Outbox = (typeof window !== 'undefined' && window.CalendarOutbox) || null;
      if (Outbox?.drain) {
        try {
          await Outbox.drain(R);
        } catch (e: any) {
          console.warn('[calendar-sync] outbox drain failed:', e);
        }
      }

      await syncCalendarToMarkdown();
      await syncMarkdownToCalendar();
      // Refresh the schedule timeline now that sync has rewritten daily notes.
      // (Previously gated on a never-assigned window global, so it never ran;
      // now a direct ESM call — see the phantom-reader ratchet.)
      invalidateScheduleCache();
    } catch (err: any) {
      console.error('Calendar sync error:', err);
    } finally {
      _calendarSyncPromise = null;
    }
  })();
  return _calendarSyncPromise;
}

// Hand a transient provider error to the outbox. Returns true when the
// error was queued (caller treats it as "not fatal"); false for hard
// failures the caller should surface to the user via sendNotification.
/**
 * @param {any} spec - the failure spec passed to Outbox.encodeFailure.
 * @param {any} err - the provider error.
 * @returns {Promise<boolean>} true when queued (non-fatal).
 */
async function _maybeEnqueueFailure(spec: any, err: any) {
  const Outbox = (typeof window !== 'undefined' && window.CalendarOutbox) || null;
  if (!Outbox?.encodeFailure || !Outbox.enqueue) return false;
  const entry = Outbox.encodeFailure(spec, err);
  if (!entry) return false;
  try {
    await Outbox.enqueue(entry);
    return true;
  } catch (e: any) {
    console.warn('[calendar-sync] outbox enqueue failed:', e);
    return false;
  }
}

// ── Lifecycle ────────────────────────────────────────────────────────────────

/** @returns {Promise<void>} */
async function startCalendarSync() {
  // The outbox's own 30 s drain timer and `online`-event handler need
  // to be wired even when the 5-minute sync isn't running — that's how
  // network-recovery flushes queued writes the moment connectivity
  // returns instead of sitting idle for up to 5 minutes.
  const Outbox = (typeof window !== 'undefined' && window.CalendarOutbox) || null;
  if (Outbox?.start) {
    try {
      Outbox.start(_registry());
    } catch (e: any) {
      console.warn('[calendar-sync] outbox.start failed:', e);
    }
  }
  await runCalendarSync();
  if (_calendarSyncTimer) clearInterval(_calendarSyncTimer);
  _calendarSyncTimer = setInterval(runCalendarSync, CALENDAR_SYNC_INTERVAL);
}

/** @returns {void} */
function stopCalendarSync() {
  if (_calendarSyncTimer) {
    clearInterval(_calendarSyncTimer);
    _calendarSyncTimer = null;
  }
  const Outbox = (typeof window !== 'undefined' && window.CalendarOutbox) || null;
  if (Outbox?.stop) {
    try {
      Outbox.stop();
    } catch {}
  }
}

// ── Auto-start ───────────────────────────────────────────────────────────────
// v2 works on every platform (web, Electron, iOS), so the start hook is no
// longer gated on Capacitor. We still wait for `encryption:ready` so we
// don't read ciphertext on a vault that hasn't unlocked yet.

let _calendarStarted = false;

// Idempotent auto-start gate. Module-private: the auto-start is driven entirely
// by this file's own `encryption:ready` listener (+ the 8s fallback / `resume`
// hooks below), so nothing outside calendar-sync ever needs to call it.
/** @returns {void} */
function startCalendarSyncIfNeeded() {
  if (_calendarStarted) return;
  if (typeof window !== 'undefined' && window._encryption?.needsKey && !window._encryption?.active)
    return;
  _calendarStarted = true;
  startCalendarSync();
}

if (typeof window !== 'undefined') {
  window.addEventListener(
    'encryption:ready',
    () => {
      startCalendarSyncIfNeeded();
    },
    { once: true }
  );
  setTimeout(() => startCalendarSyncIfNeeded(), 8000);

  document.addEventListener('resume', () => {
    if (_calendarStarted) setTimeout(runCalendarSync, 1000);
    else startCalendarSyncIfNeeded();
  });
  document.addEventListener('pause', stopCalendarSync);
}

// ── Exports ──────────────────────────────────────────────────────────────
// Phase 4.5 ES-modules cutover: classic-script bare-name callers in other
// (still-classic) source files keep working via top-level `function`
// declarations (which, in classic-script mode, leak to window — see the
// window.* shim block below for the module-mode equivalent). Tests use
// the named exports directly through the jest esbuild transformer.

/** @returns {void} */
function _resetForTesting() {
  _calendarSyncPromise = null;
  _providerLocks.clear();
  _invalidateTitleMap();
}

export {
  _metaKey,
  _resetForTesting,
  buildMdLine,
  CALENDAR_METADATA_SCHEMA_VERSION,
  dailyNoteName,
  dateToYYMMDD,
  findEventLine,
  hhmmToTime,
  insertEventsUnderHeading,
  loadMetadataStore,
  parseMarkdownEvents,
  saveMetadataStore,
  syncCalendarToMarkdown,
  syncMarkdownToCalendar,
  yymmddToDate,
};
